import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  locale?: string;
  bio?: string;
  avatar?: string;
  role: string;
  status: string;
  addresses?: Address[];
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  id: string;
  userId: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  isPrimary: boolean;
  createdAt: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  locale?: string;
  bio?: string;
  avatarFile?: File;
  currentPassword?: string;
  newPassword?: string;
}

export interface UpdateAddressData {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  isPrimary?: boolean;
}

export const profileService = {
  /**
   * Get current user's profile
   */
  getProfile: async (): Promise<UserProfile> => {
    const response = await axiosInstance.get<UserProfile>(
      API_ENDPOINTS.USER_PROFILE.GET_PROFILE
    );
    return response.data;
  },

  /**
   * Unified profile update - handles everything in one call
   */
  updateProfile: async (data: UpdateProfileData): Promise<UserProfile> => {
    const formData = new FormData();

    // Add text fields
    if (data.firstName) formData.append('firstName', data.firstName);
    if (data.lastName) formData.append('lastName', data.lastName);
    if (data.email) formData.append('email', data.email);
    if (data.phone) formData.append('phone', data.phone);
    if (data.locale) formData.append('locale', data.locale);
    if (data.bio) formData.append('bio', data.bio);

    // Add password fields if provided
    if (data.currentPassword)
      formData.append('currentPassword', data.currentPassword);
    if (data.newPassword) formData.append('newPassword', data.newPassword);

    // Add avatar file if provided
    if (data.avatarFile) formData.append('avatar', data.avatarFile);

    const response = await axiosInstance.patch<UserProfile>(
      API_ENDPOINTS.USER_PROFILE.UPDATE_PROFILE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Update basic profile info only (legacy endpoint)
   */
  updateProfileBasic: async (data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    locale?: string;
    bio?: string;
  }): Promise<UserProfile> => {
    const response = await axiosInstance.patch<UserProfile>(
      API_ENDPOINTS.USER_PROFILE.UPDATE_BASIC,
      data
    );
    return response.data;
  },

  /**
   * Upload avatar only (legacy endpoint)
   */
  uploadAvatar: async (file: File): Promise<UserProfile> => {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await axiosInstance.post<UserProfile>(
      API_ENDPOINTS.USER_PROFILE.UPLOAD_AVATAR,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Update password only (legacy endpoint)
   */
  updatePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.patch(
      API_ENDPOINTS.USER_PROFILE.UPDATE_PASSWORD,
      { currentPassword, newPassword }
    );
    return response.data;
  },

  /**
   * Get all addresses
   */
  getAddresses: async (): Promise<Address[]> => {
    const response = await axiosInstance.get<Address[]>(
      API_ENDPOINTS.USER_PROFILE.GET_ADDRESSES
    );
    return response.data;
  },

  /**
   * Add a new address
   */
  addAddress: async (address: UpdateAddressData): Promise<Address> => {
    const response = await axiosInstance.post<Address>(
      API_ENDPOINTS.USER_PROFILE.ADD_ADDRESS,
      address
    );
    return response.data;
  },

  /**
   * Update an existing address
   */
  updateAddress: async (
    addressId: string,
    address: UpdateAddressData
  ): Promise<Address> => {
    const response = await axiosInstance.patch<Address>(
      API_ENDPOINTS.USER_PROFILE.UPDATE_ADDRESS(addressId),
      address
    );
    return response.data;
  },

  /**
   * Delete an address
   */
  deleteAddress: async (addressId: string): Promise<Address> => {
    const response = await axiosInstance.delete<Address>(
      API_ENDPOINTS.USER_PROFILE.DELETE_ADDRESS(addressId)
    );
    return response.data;
  },

  /**
   * Delete user account permanently
   * This will permanently delete the user's account and all associated data
   */
  deleteAccount: async (): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete<{
      success: boolean;
      message: string;
    }>(API_ENDPOINTS.USER_PROFILE.DELETE_ACCOUNT);
    return response.data;
  },
};
